import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {restrictToFirstScrollableAncestor} from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {IconButton} from '@dropbox/dig-components/buttons';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Split, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {CloseLine, DragHandleLine, WorkstreamsLine} from '@dropbox/dig-icons/assets';
import {snackbarAtom} from 'atoms/snackbar';
import {WorkstreamBasic} from 'client';
import {Avatar} from 'components/DSYS/Avatar';
import {t} from 'i18next';
import {useSetAtom} from 'jotai';
import {Dispatch, SetStateAction, useEffect, useMemo, useRef} from 'react';

import {getSubworkstreamSelectionErrorKey} from './subworkstreamValidation';
import {getWorkstreamDri} from './utils';
import {WorkstreamTypeahead} from './WorkstreamTypeahead';

export type WorkstreamSortWrapper = {
  id: number;
  workstreamId: number;
  data: WorkstreamBasic | undefined;
};

export const getUniqueSelectedWorkstreamIds = (items: WorkstreamSortWrapper[]): number[] => {
  const seen = new Set<number>();

  return items
    .map(({workstreamId}) => workstreamId)
    .filter((workstreamId) => {
      if (workstreamId <= 0 || seen.has(workstreamId)) {
        return false;
      }

      seen.add(workstreamId);
      return true;
    });
};

const getNextWorkstreamSortWrapperId = (items: WorkstreamSortWrapper[]): number =>
  items.reduce((maxId, item) => Math.max(maxId, item.id), 0) + 1;

export const ReordableWorkstreams = ({
  autoFocus,
  setAutoFocus,
  workstreams,
  setWorkstreams,
  blockedWorkstreamIds = [],
  getDisabledReason,
}: {
  autoFocus: boolean;
  setAutoFocus: (value: boolean) => void;
  workstreams: WorkstreamSortWrapper[];
  setWorkstreams: Dispatch<SetStateAction<WorkstreamSortWrapper[]>>;
  blockedWorkstreamIds?: number[];
  // Why a candidate can't be added (e.g. the same-timeframe rule); already added ones stay.
  getDisabledReason?: (workstream: WorkstreamBasic) => string | undefined;
}) => {
  const isFirstRender = useRef(true);
  const setSnackbarMessage = useSetAtom(snackbarAtom);
  const selectedIds = useMemo(
    () => [...new Set([...blockedWorkstreamIds, ...getUniqueSelectedWorkstreamIds(workstreams)])],
    [blockedWorkstreamIds, workstreams]
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (!isFirstRender.current && workstreams.some((item) => !item.data)) {
      return;
    }

    setWorkstreams((items) => {
      if (items.some((item) => !item.data)) {
        return items;
      }

      return [
        ...items,
        {
          id: getNextWorkstreamSortWrapperId(items),
          workstreamId: -1,
          data: undefined,
        },
      ];
    });
    isFirstRender.current = false;
  }, [setWorkstreams, workstreams]);

  function handleDragEnd(event: DragEndEvent) {
    const {active, over} = event;

    if (active.id !== over?.id) {
      setWorkstreams((items) => {
        const oldIndex = items.findIndex((entry) => entry.id === active.id);
        const newIndex = items.findIndex((entry) => entry.id === over?.id);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }

  return (
    <Stack gap="12">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToFirstScrollableAncestor]}
      >
        <SortableContext items={workstreams} strategy={verticalListSortingStrategy}>
          {workstreams.map((workstream, index) =>
            !workstream.data ? (
              <Box key={workstream.id} width="100%">
                <WorkstreamTypeahead
                  autoFocus={autoFocus && index === workstreams.length - 1}
                  selectedIds={selectedIds}
                  getDisabledReason={getDisabledReason}
                  onSelect={(selectedWorkstream) => {
                    const validationErrorKey = getSubworkstreamSelectionErrorKey({
                      candidateId: selectedWorkstream.id,
                      blockedWorkstreamIds,
                      selectedWorkstreamIds: getUniqueSelectedWorkstreamIds(workstreams),
                    });

                    if (validationErrorKey) {
                      setSnackbarMessage({text: t(validationErrorKey)});
                      return;
                    }

                    setAutoFocus(false);
                    setWorkstreams((items) => {
                      const selectedIndex = items.findIndex((item) => item.id === workstream.id);
                      if (selectedIndex === -1) {
                        return items;
                      }

                      if (
                        blockedWorkstreamIds.includes(selectedWorkstream.id) ||
                        items.some(
                          (item) =>
                            item.id !== workstream.id && item.workstreamId === selectedWorkstream.id
                        )
                      ) {
                        return items;
                      }

                      const newItems = [...items];
                      newItems[selectedIndex] = {
                        ...newItems[selectedIndex],
                        workstreamId: selectedWorkstream.id,
                        data: selectedWorkstream,
                      };

                      return [
                        ...newItems,
                        {
                          id: getNextWorkstreamSortWrapperId(newItems),
                          workstreamId: -1,
                          data: undefined,
                        },
                      ];
                    });
                  }}
                />
              </Box>
            ) : (
              <ReorderableWorkstreamRow
                key={workstream.id}
                row={workstream}
                onDelete={(id) => setWorkstreams((items) => items.filter((item) => item.id !== id))}
              />
            )
          )}
        </SortableContext>
      </DndContext>
    </Stack>
  );
};

const ReorderableWorkstreamRow = ({
  row,
  onDelete,
}: {
  row: WorkstreamSortWrapper;
  onDelete: (id: number) => void;
}) => {
  const {attributes, isSorting, listeners, transform, setNodeRef, transition, isDragging} =
    useSortable({
      id: row.id,
    });

  const style = {
    transition,
    transform: `translate3d(${transform?.x ?? 0}px, ${transform?.y ?? 0}px, 0)`,
    zIndex: isDragging ? 1 : undefined,
    opacity: isSorting && !isDragging ? 0.7 : 1,
  };

  const owner = getWorkstreamDri(row.data);

  return (
    <>
      <Box
        backgroundColor="Background Base"
        borderRadius="Small"
        boxShadow={isDragging ? 'Floating' : undefined}
        borderColor="Border Subtle"
        borderWidth="1"
        borderStyle="Solid"
        position="relative"
        padding="12"
        ref={setNodeRef}
        style={style}
      >
        <Split gap="12" alignY="center">
          <Box
            position="absolute"
            color={isDragging ? 'Border Base' : 'Border Subtle'}
            style={{left: -26, top: 12, cursor: isDragging ? 'grabbing' : 'grab'}}
            {...attributes}
            {...listeners}
            tabIndex={-1}
          >
            <UIIcon src={DragHandleLine} size="medium" className={atoms({color: 'Border Base'})} />
          </Box>

          <Split.Item paddingTop="4">
            <UIIcon src={WorkstreamsLine} />
          </Split.Item>

          <Split.Item width="fill">
            <Text>{row.data?.name}</Text>
          </Split.Item>

          {owner && (
            <Split.Item>
              <Avatar size="small" user={owner} />
            </Split.Item>
          )}

          <Split.Item>
            <IconButton
              variant="borderless"
              size="medium"
              shape="circular"
              onClick={() => onDelete(row.id)}
              aria-label="icon button"
            >
              <UIIcon src={CloseLine} />
            </IconButton>
          </Split.Item>
        </Split>
      </Box>
    </>
  );
};
